import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/useAuth'
import { NolosMark, NolosWordmark } from '../../design/Logo'
import '../../design/fonts.css'
import '../../design/tokens.css'
import './login.css'

const FILM_URL = '/films/nolos-cinema-46s.mp4'
const DEMO_URL = 'https://calendly.com/alessandro-nolos/30min'
const ACCESS_EMAIL = 'mailto:info@nolos.ch?subject=NOLOS%20access%20request'

/** The product film opens in a large modal at about three quarters of the
 * screen (Ale, 3 Nov 2026: "the video should be a button actually that plays
 * full screen or like 3/4 of screen"). One shared native <dialog> (opened
 * with showModal) sits outside the breakpoint-hidden layout, so it survives
 * a resize, keeps focus inside and makes the page behind it inert. Nothing
 * downloads until the button is pressed; Escape, Close or a click on the
 * backdrop closes it, and focus returns to the button that opened it. */
function FilmButton({
  variant,
  onOpen,
}: {
  variant: 'panel' | 'below'
  onOpen: (opener: HTMLButtonElement) => void
}) {
  return (
    <button
      type="button"
      className={`lb-film-button ${variant}`}
      onClick={(event) => onOpen(event.currentTarget)}
    >
      <span className="lb-film-icon" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
          <path d="M6 3 17 10 6 17Z" />
        </svg>
      </span>
      Watch the film
    </button>
  )
}

function FilmDialog({ opener, onClosed }: { opener: HTMLButtonElement; onClosed: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null)
  const player = useRef<HTMLVideoElement>(null)
  const [failed, setFailed] = useState(false)
  const [ended, setEnded] = useState(false)

  useEffect(() => {
    const node = dialog.current
    const video = player.current
    if (!node) return
    if (!node.open) node.showModal()
    // Opened by a click, so playback with sound is allowed; if the browser
    // still refuses, the native controls stay available.
    void video?.play().catch(() => undefined)
    const scroll = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      video?.pause()
      document.body.style.overflow = scroll
      if (node.open) node.close()
    }
  }, [])

  function close() {
    dialog.current?.close()
  }

  return (
    <dialog
      ref={dialog}
      className="lb-film-dialog"
      aria-label="NOLOS product film"
      onClose={() => {
        if (dialog.current?.open) return
        onClosed()
        // A resize can hide the button that opened the film (panel vs
        // phone layout); hand focus to whichever film button is visible.
        const visible = opener.offsetParent
          ? opener
          : Array.from(document.querySelectorAll<HTMLButtonElement>('.lb-film-button')).find(
              (button) => button.offsetParent
            )
        visible?.focus()
      }}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === event.currentTarget) close()
      }}
    >
      <div className="lb-film-frame">
        <div className="lb-film-toolbar">
          {ended ? (
            <button
              type="button"
              className="lb-film-close"
              onClick={() => {
                setEnded(false)
                if (player.current) {
                  player.current.currentTime = 0
                  void player.current.play().catch(() => undefined)
                }
              }}
            >
              Watch again
            </button>
          ) : null}
          <button type="button" className="lb-film-close" onClick={close} autoFocus>
            Close
          </button>
        </div>
        {failed ? (
          <p className="lb-film-failed" role="alert">
            The film could not be loaded.{' '}
            <a href={FILM_URL} target="_blank" rel="noopener noreferrer">
              Open it in a new tab
            </a>
            .
          </p>
        ) : (
          <div className="lb-film-stage">
            <video
              ref={player}
              src={FILM_URL}
              controls={!ended}
              playsInline
              preload="auto"
              onPlay={() => setEnded(false)}
              onSeeking={() => setEnded(false)}
              onEnded={() => {
                setEnded(true)
                // Native video fullscreen cannot include the HTML link.
                // Return to the dialog at the end so the booking action is reachable.
                if (document.fullscreenElement === player.current)
                  void document.exitFullscreen().catch(() => undefined)
              }}
              onError={() => setFailed(true)}
            />
            {ended ? (
              <a
                className="lb-film-book"
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Book a demo"
                title="Book a demo (opens in a new tab)"
              />
            ) : null}
          </div>
        )}
      </div>
    </dialog>
  )
}

function AccessDialog({ opener, onClosed }: { opener: HTMLButtonElement; onClosed: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const node = dialog.current
    if (!node) return
    node.showModal()
    const scroll = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = scroll
      if (node.open) node.close()
    }
  }, [])
  return (
    <dialog
      ref={dialog}
      className="lc-access-dialog"
      aria-labelledby="login-access-title"
      onClose={() => {
        if (dialog.current?.open) return
        onClosed()
        opener.focus()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          const box = event.currentTarget.getBoundingClientRect()
          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          )
            dialog.current?.close()
        }
      }}
    >
      <div className="lc-access-head">
        <h2 id="login-access-title">Request access</h2>
        <button type="button" onClick={() => dialog.current?.close()} autoFocus>
          Close
        </button>
      </div>
      <p>Book a demo or email us about access to NOLOS.</p>
      <div className="lc-access-actions">
        <a className="lc-access-demo" href={DEMO_URL} target="_blank" rel="noopener noreferrer">
          Book a demo
        </a>
        <a href={ACCESS_EMAIL}>Email us</a>
      </div>
    </dialog>
  )
}

export default function LoginPage() {
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [filmOpener, setFilmOpener] = useState<HTMLButtonElement | null>(null)
  const [accessOpener, setAccessOpener] = useState<HTMLButtonElement | null>(null)

  // LOGIN TRACKING: api.ts sends the officer here with the reason the
  // backend gave — idled out (4 h), ended by an administrator, or the
  // ordinary expiry. One plain sentence each; no personification.
  const expiredParam = searchParams.get('expired')
  const expiredMessage =
    expiredParam === 'idle'
      ? 'You were signed out after 4 hours of inactivity.'
      : expiredParam === 'revoked'
        ? 'This session was ended by an administrator.'
        : expiredParam
          ? 'Your session has expired. Please log in again.'
          : null

  // If already authenticated, land in the v2 cockpit (front-door cutover).
  if (user) return <Navigate to="/v2" replace />

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      await login(username, password)
      navigate('/v2', { replace: true })
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'body' in err) {
        const body = (err as { body?: { detail?: string } }).body
        setError(body?.detail ?? 'Invalid credentials')
      } else {
        setError('Invalid credentials')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <aside className="login-brand">
        <div className="lb-mark">
          <NolosMark size={30} />
          <NolosWordmark className="wm" />
        </div>
        <div className="lb-hero">
          <p className="lb-lead">
            Other tools help you organize safekeeping. This one does it.
            <small>
              Collects documents, classifies them, extracts the entities, builds the ownership
              chain, applies your verification policy, and flags what's missing. The officer just
              reviews the result.
            </small>
          </p>
          <FilmButton variant="panel" onOpen={setFilmOpener} />
        </div>
      </aside>
      <main className="login-main">
        <div className="login-card">
          <div className="lc-mark">
            <NolosMark size={26} />
            <NolosWordmark className="wm" />
          </div>
          <h1>Sign in</h1>

          {expiredMessage && (
            <p className="lc-session-note" data-testid="login-expired-message" role="status">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span>{expiredMessage}</span>
            </p>
          )}

          {error && (
            <div className="lc-note error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="lc-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <p className="lc-access-prompt">
            No account?{' '}
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={(event) => setAccessOpener(event.currentTarget)}
            >
              Request access
            </button>
          </p>
          <div className="lc-film">
            <FilmButton variant="below" onOpen={setFilmOpener} />
          </div>
        </div>
      </main>
      {filmOpener ? <FilmDialog opener={filmOpener} onClosed={() => setFilmOpener(null)} /> : null}
      {accessOpener ? (
        <AccessDialog opener={accessOpener} onClosed={() => setAccessOpener(null)} />
      ) : null}
    </div>
  )
}
